import ResourceList from '../../../../components/admin/ResourceList'
import useAuth from '../../../../common/hooks/useAuth'
import { ICONS, TASK_STATUSES } from '../../../../common/constants'
import TaskItem from '../TaskItem'
import useBulkActions from './bulk-actions'
import useActions from './actions'
import getFilters from './filters'
import { useMemo } from 'react'
import useModal from '../../../../common/hooks/useModal'
import fetchAPI from '../../../../common/fetchAPI'
import useToast from '../../../../common/hooks/useToast'
import useMatchMutate from '../../../../common/hooks/useMatchMutate'
import FormModalContent from '../../../../components/common/FormModalContent'

const fields =
    'creatorId,associatedClientId,associatedClientType,assignedTeamMemberId,title,description,descriptionHtml,priority,order,userOrder,target,teamRoles,status,clientReviewStatus,timeQuoteMinutes,timeLimitMinutes,taskType,tags,createdAt,commentsCount,projectId,projectPhaseId,dueDate,timeLimitMinutes'
const populate =
    'associatedUserClient.id,associatedUserClient.firstName,associatedUserClient.lastName,associatedUserClient.role,associatedOrgClient.id,associatedOrgClient.name,associatedOrgClient.acronym,associatedOrgClient.logoUrl,assignedTeamMember.firstName,assignedTeamMember.lastName,assignedTeamMember.role,creator.firstName,creator.lastName,creator.role,project.title,project.phases'

export default function TasksList({
    title,
    onResponseLoaded,
    searchOnly,
    hideFilterTags,
    statusTabs,
    associatedClientId,
    associatedClientType,
    assignedTeamMemberId,
    newItemPath,
    targetTeam,
    targetRole,
    targetClient,
    paidOnly,
    activeStatusKeys,
    roleWithStaffAssigned,
    clientReviewStatus,
    newItemLabel,
    tag,
    renderMainButton,
    projectId,
    paginationLimit,
    getGroup,
}) {
    const auth = useAuth()
    const { onDelete } = useActions()
    const {
        onAddTags,
        onRemoveTags,
        onSetPriority,
        onSetClient,
        onSetTeamMember,
        onAttachPage,
        onSetAllToOpen,
    } = useBulkActions()

    const { setModal, isModalLoading, setModalLoading } = useModal()

    const setToast = useToast()
    const matchMutate = useMatchMutate()

    function handleSetOrder(item) {
        setModal(
            <FormModalContent
                inputs={[
                    {
                        key: 'userOrder',
                        label: 'User Order',
                        type: 'number',
                        defaultValue: item.userOrder,
                    },
                    {
                        key: 'order',
                        label: 'Global Order',
                        type: 'number',
                        defaultValue: item.order,
                    },
                ]}
                confirmText="Save"
                onConfirm={async (data, setError) => {
                    setError('')
                    if (isModalLoading) return
                    setModalLoading(true)
                    const { error, meta } = await fetchAPI(
                        `/v1/tasks/${item.id}`,
                        {
                            userOrder: data.userOrder,
                            order: data.order,
                        },
                        'PATCH',
                    )
                    setModalLoading(false)
                    if (error) {
                        setError(error, meta?.fields?.length ? [true] : [])
                        return
                    }
                    setModal(null)
                    setToast('Order updated')
                    matchMutate(new RegExp(`/tasks`))
                }}
                onCancel={() => setModal(null)}
            />,
            'Set task order',
        )
    }

    const targets = []
    if (targetTeam) {
        targets.push('team')
    }
    if (targetRole) {
        targets.push('role')
    }
    if (targetClient) {
        targets.push('client')
    }
    const targetStr = targets.join(',')

    const filters = useMemo(
        () =>
            getFilters({
                searchOnly,
                statusTabs,
                targets,
                paidOnly,
            }),
        [searchOnly, statusTabs, targets, paidOnly],
    )

    if (activeStatusKeys?.length) {
        for (const key of activeStatusKeys) {
            filters.filters[key].default = 'true'
        }
    }

    if (roleWithStaffAssigned) {
        filters.filters.roleWithStaffAssigned.default = roleWithStaffAssigned
    }

    if (clientReviewStatus && !targets.includes('client')) {
        filters.filters.clientReviewStatus.default = clientReviewStatus
    }

    if (title) {
        filters.urlKey = title
    }

    return (
        <ResourceList
            getGroup={getGroup}
            getSelectedItemText={(item) => item.title}
            togglesSelectable={auth.isAdmin && !searchOnly}
            selectLimit={10}
            allBulkSelectActions={
                auth.isAdmin &&
                (activeStatusKeys?.includes('statusAccepted') ||
                    !activeStatusKeys?.length)
                    ? [
                          {
                              title: 'Set all in-progress to open',
                              onClick: onSetAllToOpen,
                          },
                      ]
                    : undefined
            }
            bulkSelectActions={[
                {
                    title: 'Add tags',
                    onClick: onAddTags,
                    icon: ICONS.LABEL_GRAY,
                },
                {
                    title: 'Remove tags',
                    onClick: onRemoveTags,
                    icon: ICONS.LABEL_GRAY,
                },
                {
                    title: 'Set priority',
                    onClick: onSetPriority,
                },
                {
                    title: 'Set client',
                    onClick: onSetClient,
                },
                {
                    title: 'Set team member',
                    onClick: onSetTeamMember,
                },
                {
                    title: 'Attach requirements page',
                    onClick: onAttachPage,
                },
            ]}
            //
            searchPlaceholder={'Search tasks'}
            searchOnly={searchOnly}
            itemsNav={
                (auth.isAdmin || auth.isAssistant) &&
                !associatedClientId &&
                !assignedTeamMemberId
            }
            renderMainButton={
                (renderMainButton !== false && auth.isAdmin) || auth.isStaff
            }
            newItemPath={auth.isAdmin || auth.isStaff ? newItemPath : undefined}
            newItemLabel={newItemLabel || 'New task'}
            apiUrl={'/v1/tasks'}
            getItemsFromResponse={(data) => data?.results}
            itemClickPath={`/tasks/:item.id`}
            initialQuery={{
                target: targetStr || undefined,
                sortBy: searchOnly ? 'order:asc' : undefined,
                q: searchOnly ? '@@@@@@@@@@@' : undefined,
                statusComplete: searchOnly ? 'false' : undefined,
                statusCancelled: searchOnly ? 'false' : undefined,
                assignedTeamMemberId,
                associatedClientId,
                associatedClientType: associatedClientId
                    ? associatedClientType
                    : undefined,
                projectId,
                fields,
                populate,
                tags: tag || undefined,
            }}
            paginated
            paginationLimit={paginationLimit || 10}
            hideFilterTags={hideFilterTags}
            hideCount={searchOnly}
            loadMoreButton
            filters={filters}
            onResponseLoaded={onResponseLoaded}
            emptyText={searchOnly ? '' : undefined}
            fields={[
                {
                    column: 1,
                    getValue: (item) => <TaskItem task={item} />,
                },
            ]}
            actions={[
                {
                    title: 'Edit',
                    icon: ICONS.EDIT_GRAY,
                    link: '/tasks/:item.id/edit',
                    getHide: (item) =>
                        item.isAppTask ||
                        (!auth.isAdmin &&
                            !auth.isAssistant &&
                            !(
                                auth.isStaff &&
                                auth.user.id === item.creatorId &&
                                [
                                    (TASK_STATUSES.APPROVAL,
                                    TASK_STATUSES.RECREATION),
                                ].includes(item.status)
                            )),
                },
                {
                    title: 'Set order',
                    onClick: handleSetOrder,
                    getHide: (item) =>
                        item.isAppTask ||
                        item.status === TASK_STATUSES.COMPLETE ||
                        item.status === TASK_STATUSES.CANCELLED ||
                        (!auth.isAdmin &&
                            !auth.isAssistant &&
                            !(
                                auth.isStaff &&
                                auth.user.id === item.creatorId &&
                                [
                                    (TASK_STATUSES.APPROVAL,
                                    TASK_STATUSES.RECREATION),
                                ].includes(item.status)
                            )),
                },
                {
                    title: 'Delete',
                    onClick: (item) => onDelete(item),
                    hide: !auth.isAdmin && !auth.isAssistant,
                    type: 'alert',
                    icon: ICONS.TRASH_RED,
                },
            ]}
        />
    )
}
